import {storage, STORAGE_KEYS} from '@/utils/storage'
import {isNearAirport} from '@/utils/airports'
import {round1} from '@/utils/policyFormat'
import {BOOKING_CONSTRAINTS} from '@/constants/booking'

// Fallback icon for a FAQ that doesn't name its own. Every question below sets an
// `icon` matching its subject; this only catches a future FAQ added without one.
//
// Icons are picked from the existing asset set and deliberately kept to SOLID/dark
// glyphs (#201E20 / #0A0A0A / black) — they render at 14px inside the 24px mint tile
// (.hopn-ride-faq-list .icon), where hairline or multi-colour art reads badly.
const FAQ_ICON = 'label.svg'

// How many FAQs show inline under "Popular questions"; the rest live in the "More"
// popup/modal/step.
export const FAQ_INLINE_COUNT = 2

/**
 * Flow-specific FAQs, transcribed from the plugin docs/{to-airport,from-airport,
 * other-flow}.
 *
 * An answer `a` is either a string, or a `(policy) => string` function when it
 * should reflect the LIVE policy instead of the doc's hardcoded numbers. `policy`
 * holds live policy values. The booking flow passes raw numbers from
 * POLICY_CONSTANTS — cancellation/modification (freeCancellationAvailable,
 * freeCancellationHours, freeEditsCount, editableHoursBeforePickup) and waiting
 * (waitingPickupFreeSeconds, waitingPickupRateCentsPerSecond). Trip pages instead
 * pass cancellationText/modificationText from the trip's policy_popup (which has
 * no raw numbers), which the cancellation answer falls back to. Answers fall back
 * to the doc copy when no live value is available.
 */
export const FAQS_BY_FLOW = {
    // Going TO the airport (departure).
    to_airport: [
        {
            q: 'How far in advance should I book my ride to the airport?',
            icon: 'calender.svg',
            a: 'We recommend booking your ride at least 24 hours in advance to guarantee availability, especially during peak hours and holiday seasons. Last-minute bookings may be available but are subject to driver availability.',
        },
        {
            q: 'What happens if my flight departure time changes?',
            icon: 'plane-take-off-time.svg',
            a: 'No worries at all! We automatically track your flight in real time, update your booking accordingly, and notify both you and your assigned driver without you needing to do anything.',
        },
        {
            q: 'How much waiting time is included?',
            icon: 'stop-watch.svg',
            // Keep the doc wording but plug in the LIVE pickup waiting policy:
            // free_seconds -> minutes (/60), rate (cents/sec) -> $/min (*60 /100).
            a: (p) => {
                const freeSeconds = p.waitingPickupFreeSeconds;
                const rateCents = p.waitingPickupRateCentsPerSecond;
                if (freeSeconds && rateCents) {
                    const freeMinutes = round1(freeSeconds / 60);
                    const dollarsPerMinute = round1(rateCents * 60 / 100);
                    // waiting.pickup.max_wait_seconds -> minutes; fall back to 10 if absent.
                    const maxMinutes = p.waitingPickupMaxWaitSeconds ? round1(p.waitingPickupMaxWaitSeconds / 60) : 10;
                    return `Your driver may arrive early and will be ready at your scheduled pickup time. You have ${freeMinutes} minutes after your pickup time to load your luggage and get settled. After that, a $${dollarsPerMinute}/minute waiting fee applies, up to ${maxMinutes} minutes, subject to driver availability.`;
                }
                return 'Your driver may arrive early and will be ready at your scheduled pickup time. You have 5 minutes after your pickup time to load your luggage and get settled. After that, a $3/minute waiting fee applies, up to 10 minutes, subject to driver availability.';
            },
        },
        {
            q: 'Can I request extra stops on the way to the airport?',
            icon: 'point-added.svg',
            a: 'Yes. Extra stops can be requested before your trip and are subject to driver approval and an additional charge.',
        },
        {
            q: 'Can I request assistance if I have extra luggage or special needs?',
            icon: 'luggageAssistance.svg',
            a: 'Absolutely! Complimentary luggage assistance is included from your doorstep to the airport terminal curb. If you have oversized luggage or special mobility needs, simply let us know when booking so we can assign the right vehicle.',
        },
        {
            q: 'Do I need to pay in advance to confirm my booking?',
            icon: 'credit-card.svg',
            // Opens the "Pay after drop-off" view — the same content the perks list shows.
            action: 'payAfterDropoff',
            a: 'No advance payment is required! Simply provide your card details at the time of booking. You are only charged once your trip is successfully completed. We do not accept cash. We do credit/debit card payments only, keeping everything safe and simple.',
        },
        {
            q: 'Can I book my return pickup as well?',
            icon: 'return-trip-icon.svg',
            a: "Yes, absolutely! Simply add your return trip details during checkout and we'll have a driver ready for you. Your return pickup will follow the same seamless experience: flight tracked, driver assigned, and notification sent automatically.",
        },
        {
            q: 'What is the cancellation and modification policy?',
            icon: 'cancellation-changes.svg',
            // Opens the tabbed Cancellation / Modification / Refund view, which carries the
            // live policy in full — more than the one-line answer below can.
            action: 'cancellationModificationRefund',
            // Booking flow passes the live policy fields -> build exact sentences
            // from them. Trip pages only carry the curated policy text (policy_popup
            // has no raw numbers) -> fall back to that.
            a: (p) => {
                if (p.freeCancellationAvailable !== undefined || p.freeEditsCount !== undefined) {
                    // 0 / missing hours means "the policies apply", NOT "not
                    // allowed" — never claim the booking can't be cancelled/modified.
                    const cancellation = p.freeCancellationHours > 0
                        ? `You can cancel the booking up to ${p.freeCancellationHours} hour before.`
                        : 'You can cancel the booking according to the cancellation policies.';
                    const modification = p.editableHoursBeforePickup > 0
                        ? `You can modify the booking up to ${p.editableHoursBeforePickup} hour before.`
                        : 'You can update the booking according to the policies.';
                    return `${cancellation} ${modification}`;
                }
                return [p.cancellationText, p.modificationText].filter(Boolean).join(' ') || 'You can cancel or modify your booking free of charge up to 1 hour before your scheduled pickup.';
            },
        },
    ],

    // Coming FROM the airport (arrival pickup).
    from_airport: [
        {
            q: 'How do I find my driver at the airport?',
            icon: 'magnifier.svg',
            // Opens the full "finding your driver" guide popup instead of expanding
            // the inline accordion (mirrors the plugin's selectedCarDetailsLatest FAQ).
            action: 'findingDriver',
            // A FUNCTION, not a string: the support number comes from /config/app
            // (general.support_phone) and this module is evaluated at import time, long
            // before the config lands. getFaqsForFlow() calls `f.a(policy)` at render
            // time, so reading the getter in here picks up the real value. It was a
            // hardcoded 416-458-2132 until 2026-08-26.
            a: () => `<p><b>Here’s a guide on where to proceed:</b></p>
<ul>
<li>Clear Customs</li>
<li>Retrieve Your Luggage</li>
<li>Head to the Designated Posts</li>
<li>Locate Gate "A" at the Pre-arranged Limo Booth Inside</li>
<li>Provide your name to the official at the booth or call us at ${BOOKING_CONSTRAINTS.CONTACT_NUMBER || ''}</li>
</ul>
<p>No need to go outside the terminal building; stay inside in the heated/air-conditioned room. Your driver will be ready to pick you up in just a few minutes. Safe travels!</p>`,
        },
        {
            q: 'What if my flight is delayed? Will my driver still be there?',
            icon: 'flightDelay.svg',
            // Opens the flight-delayed view (the same content as the bookingReceived
            // step) instead of expanding the inline accordion — it covers both the
            // tracking and the unlimited-wait halves of the answer.
            action: 'flightDelayed',
            a: "Yes! We track your flight in real time. If your flight is delayed, your driver's arrival time is automatically adjusted to match your updated landing time. You will not be charged any extra fees due to flight delays.",
        },
        {
            q: 'How long will my driver wait for me after I arrived?',
            icon: 'stop-watch.svg',
            a: "No need to worry about time! We offer unlimited wait time — your driver will be waiting for you at the exit without any restrictions. Whether your luggage takes longer than expected or customs clearance is delayed, your driver will be there until you're ready. No extra charges, no pressure.",
        },
        {
            q: 'What should I do if I cannot find my driver at the airport?',
            icon: 'support.svg',
            // Same guide as "How do I find my driver" above — someone who can't find
            // their driver needs the walkthrough (booth location, gate, the number to
            // call), not a paragraph pointing at the app.
            action: 'findingDriver',
            a: "First, check the in-app map which shows your driver's real-time location. You can also call or message your driver directly through the app. If you are still unable to locate your driver, contact our 24/7 customer support team immediately for assistance.",
        },
        {
            q: 'Can I add a stop between the airport and my final destination?',
            icon: 'point-added.svg',
            a: `<p>Yes. If you already know you'll need a stop, you can add it before booking — simply include the stopover when setting up your trip so it's built into your route and fare from the start.</p>
<p>Need an extra stop once you're already on the ride? Just ask your driver directly. Additional stopovers during the ride are subject to the driver's approval and may incur extra charges.</p>`,
        },
        {
            q: 'What if my luggage is lost and I need to wait at the airport longer than expected?',
            icon: 'lost-item-issue.svg',
            // The answer tells them to message the driver, so the button that reaches the
            // driver is rendered under it — see `cta` in getFaqsForFlow.
            cta: 'trackDriver',
            a: `<p>No problem — your driver won't leave without you. If you're delayed at the airport dealing with lost luggage, simply message your driver directly through the in-app messenger to let them know.</p>
<p>If you need further help, you can also reach hop'n support through the mobile app or website, and our team will assist you right away.</p>`,
        },
    ],

    // City-to-city / point-to-point (no airport leg).
    others: [
        {
            q: 'How do I book a ride between two different cities or locations?',
            icon: 'black-destination.svg',
            a: 'You can book an intercity or point-to-point ride directly through the app by entering your pickup and drop-off addresses. Pricing is calculated based on distance and vehicle type. Long-distance bookings are recommended to be made at least 24 hours in advance.',
        },
        {
            q: 'Is the price fixed, or can it change after I book?',
            icon: 'label.svg',
            a: 'All point-to-point rides are offered at a fixed price confirmed at the time of booking. Your fare will not change due to traffic conditions or route variations. However, if you add extra stops or significantly change the route before the trip, adjustments may apply.',
        },
        {
            q: 'Can I book a round-trip ride between two destinations?',
            icon: 'return-trip-icon.svg',
            a: 'Yes, round-trip bookings are available and are often offered at a discounted rate. You can schedule both legs of your journey during a single booking session. You can also choose to keep the driver on standby at the destination for a fixed waiting fee.',
        },
        {
            q: 'What happens if I want to change my drop-off destination mid-trip?',
            icon: 'swap.svg',
            a: 'You can request a change in drop-off location by informing your driver directly. The fare will be recalculated based on the new destination. Significant route changes may require approval through the app before the driver can proceed.',
        },
        {
            q: 'Are there any restrictions on how far a destination-to-destination ride can be?',
            icon: 'distance.svg',
            a: 'Our service covers rides within and between cities up to a maximum distance of 500 km per trip. For distances beyond this, please contact our support team to arrange a custom long-distance transfer. Additional charges and driver rest stops may apply for very long journeys.',
        },
        {
            q: 'Can I pre-schedule a ride for an early-morning or late-night trip?',
            icon: 'simple-clock.svg',
            a: 'Absolutely! You can schedule rides as early or as late as needed, including overnight and early morning departures. We recommend booking at least 24-48 hours in advance for long-distance trips to ensure a driver is assigned and ready for you on time.',
        },
    ],
}

/**
 * Resolve the trip flow from the destinations layout [pickup, dropoff, ...stops].
 * Reads sessionStorage when no list is passed (booking flow). Trip pages pass the
 * trip's own direction (useEditTrip exposes `tripDirection`).
 */
export function getTripDirection(destinations) {
    const list = destinations || storage.get(STORAGE_KEYS.DESTINATIONS, []) || []
    const pickup = list[0]
    const dropoff = list[1]
    const pickupAirport = pickup?.lat && pickup?.lng ? !!isNearAirport(pickup.lat, pickup.lng) : false
    const dropoffAirport = dropoff?.lat && dropoff?.lng ? !!isNearAirport(dropoff.lat, dropoff.lng) : false
    return pickupAirport ? 'from_airport' : dropoffAirport ? 'to_airport' : 'others'
}

/**
 * FAQ list for a flow, with `(policy) => string` answers resolved and each item's
 * icon applied. `direction` falls back to the `others` set; `policy` is optional.
 */
export function getFaqsForFlow(direction, policy = {}) {
    const list = FAQS_BY_FLOW[direction] || FAQS_BY_FLOW.others
    return list.map((f) => ({
        q: f.q,
        a: typeof f.a === 'function' ? f.a(policy) : f.a,
        icon: f.icon || FAQ_ICON,
        action: f.action,
        // Optional: a call-to-action rendered UNDER the answer text (e.g.
        // 'trackDriver'). The answer is an HTML string, so anything with behaviour
        // has to be a real component alongside it — see FaqAccordionList.
        cta: f.cta || null,
    }))
}
